import React, { useEffect, useState } from 'react';
import './ForceStart.css';

// MUI
import { Grid, styled, TextField, Autocomplete, Chip } from '@mui/material';

// Util
import ExistingSessionInfo from './ExistingSessionInfo';
import { send_request } from '../../../../utils/Request';

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  backgroundColor: `${theme.palette.background.paper} !important`,
  minWidth: '50%',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  backgroundColor: `${theme.palette.background.paper} !important`,
  width: '100%%',
}));

const Text = styled('span')(({ textColor }) => ({
  fontSize: '14px',
  color: textColor,
}));

function SelectSessionDropdown({
  projectId,
  StyledSkeleton,
  setValues,
  values,
  placeholders,
  setIsLoading,
  setLoadingInputPHs,
  isTest,
  version,
  selected,
  setSelected,
}) {
  const [sessionIds, setSessionIds] = useState(null);
  //Added this for now. Selected wants to take in Array of strings (sessionIds) but the AutoComplete wants to take in array of objects
  //Made a second hook for now until figure out a better solution

  useEffect(() => {
    const loadRecentSessions = async () => {
      // Fetch all the existing session ids linked to the given project id
      let url = `project-service/project-session/get_recent_existing_session_ids/${projectId}`;
      if (isTest) url += '?includeTestSessions=true';

      let json = await send_request(url, null, null, 'GET');
      let dropdownTitleArray = [];
      const responseProjectSessions = json?.data;
      if (!json || !responseProjectSessions) {
        setSessionIds(dropdownTitleArray);
        return;
      } else {
        dropdownTitleArray = responseProjectSessions;
      }
      setSessionIds(dropdownTitleArray);
    };
    loadRecentSessions();
  }, []);

  useEffect(() => {
    if (selected?.length === 1 && selected[0]) {
      handleFetchExisting(selected[0]);
    } else {
      setValues(null);
    }
  }, [selected]);

  // Function for fetching existing placeholder data values and setting
  // It into the state
  const handleFetchExisting = async selectedId => {
    if (!selectedId) return;

    setIsLoading(true);
    setLoadingInputPHs(true);

    const url = `project-service/project-session/get_existing_session_placeholder_data/${selectedId}`;
    let json = await send_request(url, placeholders, '', 'POST');

    if (!json || !json.data) {
      setIsLoading(false);
      setLoadingInputPHs(false);
      return;
    }

    // Transform the response data to maintain the correct structure
    const transformedValues = {};
    for (const key in json.data) {
      const placeholder = placeholders.find(p => p.key === key);
      if (placeholder) {
        const placeholderType = placeholder.type;
        const placeholderData = json.data[key];
        transformedValues[key] = {
          type: placeholderType,
          value:
            placeholderType === 'datetime'
              ? {
                  rawValue: placeholderData?.rawValue || null,
                  format: 'yyyy-MM-DD hh:MM:ssZ',
                  value: placeholderData?.value || '',
                }
              : placeholderData,
        };
      }
    }

    setValues(transformedValues);
    setIsLoading(false);
    setLoadingInputPHs(false);

    return transformedValues;
  };

  const handleSelectionChange = (event, newValue) => {
    const newSessionIdList = [
      ...new Set(newValue.filter(session => session).map(session => session.sessionId || session)),
    ];

    setSelected(newSessionIdList);
  };

  const handlePaste = event => {
    const pastedText = event.clipboardData.getData('text');

    if (pastedText.includes(',')) {
      event.preventDefault();

      const pastedIds = pastedText
        .split(',')
        .map(id => id.trim())
        .filter(id => id.length > 0);

      if (pastedIds.length > 0) {
        const combinedIds = [...new Set([...selected, ...pastedIds])];
        setSelected(combinedIds);
      }
    }
  };

  const categorizeByDate = session => {
    const lastUpdated =
      session?.lastUpdated || session?.createdAt || session?.date || session?.timestamp;

    if (!lastUpdated) {
      return 'Other';
    }

    const sessionDate = new Date(lastUpdated);
    if (isNaN(sessionDate.getTime())) {
      return 'Other';
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const sessionDateOnly = new Date(
      sessionDate.getFullYear(),
      sessionDate.getMonth(),
      sessionDate.getDate()
    );

    if (sessionDateOnly.getTime() === today.getTime()) {
      return 'Today';
    }

    if (sessionDateOnly.getTime() === yesterday.getTime()) {
      return 'Yesterday';
    }

    return sessionDateOnly.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const availableOptions = sessionIds
    ? sessionIds
        .filter(session => {
          const sessionId = session?.sessionId || session;
          return !selected.includes(sessionId);
        })
        .map(session => ({
          ...session,
          group: categorizeByDate(session),
        }))
    : [];

  const sortedOptions = availableOptions.sort((a, b) => {
    const aDate = new Date(a.lastUpdated || a.createdAt || a.date || a.timestamp || 0);
    const bDate = new Date(b.lastUpdated || b.createdAt || b.date || b.timestamp || 0);

    return bDate.getTime() - aDate.getTime();
  });

  return sessionIds ? (
    <Grid container rowGap={2} marginBottom={1}>
      <Grid item xs={12}>
        <Grid container rowGap={2}>
          <Grid item xs={12}>
            <b>Select session</b>
          </Grid>
          <Grid item xs={12}>
            <StyledAutocomplete
              className="force-start-autocomplete"
              multiple
              freeSolo
              disableCloseOnSelect
              variant="standard"
              options={sortedOptions}
              groupBy={option => option.group}
              getOptionLabel={option => {
                if (!option) return '';
                return `${option?.sessionId} (${option?.timeAgo})`;
              }}
              onChange={handleSelectionChange}
              value={selected}
              renderInput={params => (
                <StyledTextField
                  {...params}
                  size="small"
                  placeholder="Type in or search for session IDs (paste comma-separated IDs)"
                  onPaste={handlePaste}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((selectedOption, index) => {
                  return <Chip label={selectedOption} {...getTagProps({ index })} />;
                })
              }
            />
          </Grid>
        </Grid>
      </Grid>
      {selected.length > 0 && (
        <Grid item xs={12}>
          {selected.map(sessionId => (
            <ExistingSessionInfo
              key={sessionId}
              selected={sessionId}
              projectId={projectId}
              version={version}
            />
          ))}
        </Grid>
      )}
    </Grid>
  ) : (
    <StyledSkeleton variant="rectangular" height="35px" />
  );
}

export default SelectSessionDropdown;
